import type { Workout } from '../workouts/types';
import type { Category } from '../scoring/types';
import { awardBadges, totalOfBests, type FeedBadges, type RankMovement } from './badges';

export type { FeedBadges } from './badges';

export interface FeedItem {
  workout: Workout;
  displayName: string;
  badges: FeedBadges;
  /** Set only when this lift moved its lifter up that month's leaderboard. */
  rankMovement?: RankMovement;
}

/** Chronological order: date first, then whichever was recorded first that day. */
function ascending(a: Workout, b: Workout): number {
  const byDate = a.workoutDate.localeCompare(b.workoutDate);
  if (byDate !== 0) return byDate;
  return (a.createdAt?.getTime?.() ?? 0) - (b.createdAt?.getTime?.() ?? 0);
}

/** Running records for one lifter as the walk moves forward through time. */
interface Records {
  /** Best points per category, all time. */
  categoryPoints: Partial<Record<Category, number>>;
  /** Best points per category within one month, keyed `category|monthKey`. */
  categoryMonthPoints: Record<string, number>;
  /** Best estimated 1RM per exercise, all time. */
  exercise1Rm: Record<string, number>;
}

function emptyRecords(): Records {
  return { categoryPoints: {}, categoryMonthPoints: {}, exercise1Rm: {} };
}

/** One month's standings so far: lifter to their best points in each category. */
type Board = Map<string, Partial<Record<Category, number>>>;

/**
 * Where a lifter stands on a board, by monthly total.
 *
 * Standard competition ranking - equal totals share a rank. Someone with no
 * lifts yet this month is not on the board at all, and counts as sitting one
 * place below everyone who is, so their first lift only reads as a climb if it
 * actually overtook somebody.
 */
function rankOf(board: Board, userId: string): number {
  const mine = board.get(userId);
  if (!mine) return board.size + 1;
  const myTotal = totalOfBests(mine);
  let ahead = 0;
  for (const [otherId, bests] of board) {
    if (otherId !== userId && totalOfBests(bests) > myTotal) ahead += 1;
  }
  return ahead + 1;
}

/**
 * Turns raw workouts into the activity feed, newest first, marking which lifts
 * were records when they happened and which ones moved someone up the board.
 *
 * Badges need every earlier lift by the same person, and rank movement needs
 * every earlier lift by everyone, so this takes the full set and walks it
 * forwards once. That is fine for a friend group; if the collection ever
 * outgrows loading in one go, the fix is to compute these flags when a workout
 * is written rather than to sample the history here.
 */
export function buildFeed(workouts: Workout[], userNames: Record<string, string>): FeedItem[] {
  const chronological = [...workouts].sort(ascending);
  const byUser = new Map<string, Records>();
  /** monthKey -> that month's standings as of the walk's current position. */
  const boards = new Map<string, Board>();
  const items: FeedItem[] = [];

  for (const workout of chronological) {
    let records = byUser.get(workout.userId);
    if (!records) {
      records = emptyRecords();
      byUser.set(workout.userId, records);
    }

    const monthCategoryKey = `${workout.category}|${workout.monthKey}`;
    const badges = awardBadges(workout, {
      categoryPoints: records.categoryPoints[workout.category],
      categoryMonthPoints: records.categoryMonthPoints[monthCategoryKey],
      exercise1Rm: records.exercise1Rm[workout.exerciseId],
    });

    // Rank is measured either side of applying this lift to its month's board.
    let board = boards.get(workout.monthKey);
    if (!board) {
      board = new Map();
      boards.set(workout.monthKey, board);
    }
    const rankBefore = rankOf(board, workout.userId);

    const myBests = board.get(workout.userId) ?? {};
    if (workout.points > (myBests[workout.category] ?? -Infinity)) {
      board.set(workout.userId, { ...myBests, [workout.category]: workout.points });
    } else if (!board.has(workout.userId)) {
      board.set(workout.userId, myBests);
    }

    const rankAfter = rankOf(board, workout.userId);

    items.push({
      workout,
      displayName: userNames[workout.userId] ?? 'Unknown User',
      badges,
      ...(rankAfter < rankBefore ? { rankMovement: { from: rankBefore, to: rankAfter } } : {}),
    });

    records.categoryPoints[workout.category] = Math.max(
      records.categoryPoints[workout.category] ?? -Infinity,
      workout.points,
    );
    records.categoryMonthPoints[monthCategoryKey] = Math.max(
      records.categoryMonthPoints[monthCategoryKey] ?? -Infinity,
      workout.points,
    );
    if (workout.exerciseId) {
      records.exercise1Rm[workout.exerciseId] = Math.max(
        records.exercise1Rm[workout.exerciseId] ?? -Infinity,
        workout.estimated1RmLb,
      );
    }
  }

  return items.reverse();
}
